export function isEmptyObject(obj: Record<string, any>): boolean {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
}

export function isUndefinedOrNullOrEmptyObject(value: any): boolean {
  return value === undefined || value === null || isEmptyObject(value);
}

export function isUndefinedOrNullOrEmptyString(value: any): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Format a value as a local date-only string (YYYY-MM-DD). Uses local date parts for Date inputs
 * to avoid the UTC shift `toISOString()` introduces (local midnight can roll back a day in
 * positive-UTC timezones), which matters for DOB-driven minor-document rules. Strings are assumed
 * date-like and truncated at the time component.
 */
export function toLocalDateOnly(value: string | Date): string {
  if (typeof value === 'string') return value.split('T')[0];
  const mm = String(value.getMonth() + 1).padStart(2, '0');
  const dd = String(value.getDate()).padStart(2, '0');
  return `${value.getFullYear()}-${mm}-${dd}`;
}

export function maskSSN(ssn: string | undefined | null): string {
  if (!ssn) return '';
  const cleanSSN = ssn.replace(/\D/g, '');
  if (cleanSSN.length < 4) return ssn;
  const last4 = cleanSSN.slice(-4);
  return `XXX-XX-${last4}`;
}

export function formatDocumentTitle(documentTitle: string | undefined | null): string {
  if (!documentTitle) return '';
  return documentTitle.replace(/_/g, ' ').toUpperCase();
}

export const flattenPhoneNumber = (phoneNumber: string) => {
  return phoneNumber.replace(/\D/g, '');
};

export const isWithinYears = (date: Date, years: number): boolean => {
  const currentDate = new Date();
  const futureDate = new Date(date);
  futureDate.setFullYear(futureDate.getFullYear() + years);
  return currentDate < futureDate;
};

export const formatDate = (date: Date) => {
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric',timeZone: 'UTC' });
};

export const decodeBase64 = (base64: string | undefined | null) => {
  if (!base64) return undefined;
  try {
    const binString = atob(base64);
    const bytes = Uint8Array.from(binString, (m) => m.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return undefined;
  }
};

export const safeDateParse = (dateString: string | undefined | null): Date | null => {
  if (!dateString) return null;
  const parsedDate = new Date(dateString);
  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

/**
 * Reorder a signer's display name to the order the Form I-9 certification cell reads —
 * "Last Name, First Name and Title of Employer or Authorized Representative".
 *
 * The stored signer name is a single string: new platform signatures arrive already ordered
 * ("Raye, Esther (Remote Section 2 Completer)"), but historical rows and typed signer names are
 * free text ("Esther Raye (Remote Section 2 Completer)"). A string that already contains a comma
 * is shown verbatim; otherwise a trailing parenthesised title is peeled off, the last word is
 * treated as the last name, and the rest keeps its order. Best-effort by design (VTK-1942) — in
 * free text a multi-word last name cannot be told apart from middle names.
 */
export const formatSignerNameLastFirst = (name: string | undefined | null): string => {
  const raw = (name ?? '').trim();
  if (!raw || raw.includes(',')) return raw;
  const titleMatch = raw.match(/\s*(\([^)]*\))\s*$/);
  const title = titleMatch ? ` ${titleMatch[1]}` : '';
  const bare = titleMatch ? raw.slice(0, titleMatch.index).trim() : raw;
  const words = bare.split(/\s+/).filter(Boolean);
  if (words.length < 2) return `${bare}${title}`.trim();
  const last = words[words.length - 1];
  const rest = words.slice(0, -1).join(' ');
  return `${last}, ${rest}${title}`;
};

/**
 * Normalize a stored one-line address for display: drop blank components, so a blank address2
 * persisted before the address composition fix (VTK-1942) cannot render a double comma.
 */
export const normalizeAddressDisplay = (address: string | undefined | null): string =>
  (address ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ');
